/**
 * HospitalHeader - Header component with hospital selector and action buttons
 */
import React, { useState } from 'react';
import { Building2, ChevronDown, Search, Plus, Pencil, Trash2, Upload, FileSpreadsheet, Save, X as XIcon, Info, RotateCcw } from 'lucide-react';
import type { Hospital } from '../../hooks/useHospitalData';
import { HospitalInfoModal } from './HospitalInfoModal';
import PermissionGate, { usePermissoesTela } from '../PermissionGate';

interface HospitalHeaderProps {
  // Hospital data
  hospitais: Hospital[];
  selectedHospital: Hospital | null;
  filteredHospitais: Hospital[];
  loading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Story 2.62: inativos (MASTER)
  isMaster?: boolean;
  showInactive?: boolean;
  onShowInactiveChange?: (show: boolean) => void;
  onReactivate?: () => void;

  // Dropdown
  showHospitalDropdown: boolean;
  toggleHospitalDropdown: () => void;
  closeHospitalDropdown: () => void;
  onSelectHospital: (hospital: Hospital) => void;

  // Edit mode
  isEditing: boolean;
  saving: boolean;

  // Actions
  onCreateNew: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSave: () => void;
  onCancel: () => void;
  onOpenImportModal: () => void;
  onExportExcel: () => void;
}

export const HospitalHeader: React.FC<HospitalHeaderProps> = ({
  hospitais,
  selectedHospital,
  filteredHospitais,
  loading,
  searchTerm,
  setSearchTerm,
  isMaster,
  showInactive,
  onShowInactiveChange,
  onReactivate,
  showHospitalDropdown,
  toggleHospitalDropdown,
  closeHospitalDropdown,
  onSelectHospital,
  isEditing,
  saving,
  onCreateNew,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  onOpenImportModal,
  onExportExcel,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { podeCriar, podeEditar } = usePermissoesTela('HOSPITAIS_LIST');
  // isEditing is true both when creating (id===0) and editing an existing hospital.
  // We detect "creating" by absence of a selectedHospital (new form) — but the component
  // doesn't receive isCreating directly. The safest signal: no selectedHospital + isEditing.
  const isCreating = isEditing && !selectedHospital;
  const podeSalvar = isCreating ? podeCriar : podeEditar;

  return (
    <div className="mb-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-end justify-between gap-4">
          {/* Hospital Selector with Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <strong>Hospital:</strong>
            </label>
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div className="relative flex-1 max-w-md">
                <button
                  onClick={toggleHospitalDropdown}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <span className="truncate text-gray-900 dark:text-white">
                    {selectedHospital ? selectedHospital.nome_fantasia : 'Selecione um hospital...'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
                </button>

                {/* Dropdown with search */}
                {showHospitalDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Buscar por código, nome, CNPJ, cidade..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          autoFocus
                        />
                      </div>
                      {/* Story 2.62: MASTER pode exibir estabelecimentos inativos */}
                      {isMaster && (
                        <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!showInactive}
                            onChange={(e) => onShowInactiveChange?.(e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          Mostrar inativos
                        </label>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {loading ? (
                        <div className="p-3 text-center text-gray-500 text-sm">Carregando...</div>
                      ) : filteredHospitais.length === 0 ? (
                        <div className="p-3 text-center text-gray-500 text-sm">Nenhum hospital encontrado</div>
                      ) : (
                        filteredHospitais.map((hospital) => (
                          <button
                            key={hospital.id}
                            onClick={() => {
                              onSelectHospital(hospital);
                              closeHospitalDropdown();
                              setSearchTerm('');
                            }}
                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                              selectedHospital?.id === hospital.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <div className={`font-medium ${hospital.ativo ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                              {hospital.nome_fantasia}
                              {!hospital.ativo && <span className="ml-1.5 text-xs not-italic text-amber-600 dark:text-amber-400">(Inativo)</span>}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              <span className="mr-3">Cliente: {hospital.cliente_nome_fantasia || hospital.cliente_razao_social || '-'}</span>
                              {hospital.codigo && <span className="mr-3">Código: {hospital.codigo}</span>}
                              {hospital.cnpj && <span className="mr-3">CNPJ: {hospital.cnpj}</span>}
                              {hospital.cidade && hospital.estado && <span>{hospital.cidade}/{hospital.estado}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500">({hospitais.length} hospitais)</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <PermissionGate tela="HOSPITAIS_LIST" acao="criar">
                  <button
                    onClick={onCreateNew}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" /> Novo
                  </button>
                </PermissionGate>
                <PermissionGate tela="HOSPITAIS_LIST" acao="editar">
                  <button
                    onClick={onEdit}
                    disabled={!selectedHospital}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Pencil className="w-4 h-4" /> Editar
                  </button>
                </PermissionGate>
                <PermissionGate tela="HOSPITAIS_LIST" acao="excluir">
                  <button
                    onClick={onDelete}
                    disabled={!selectedHospital}
                    className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Trash2 className="w-4 h-4" /> Excluir
                  </button>
                </PermissionGate>
                {/* Story 2.62: MASTER reativa estabelecimento desativado */}
                {isMaster && selectedHospital && !selectedHospital.ativo && (
                  <button
                    onClick={onReactivate}
                    className="px-3 py-1.5 text-sm border border-amber-400 dark:border-amber-600 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center gap-1.5"
                  >
                    <RotateCcw className="w-4 h-4" /> Reativar
                  </button>
                )}
                <PermissionGate tela="HOSPITAIS_LIST" acao="editar">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={onOpenImportModal}
                      className="px-3 py-1.5 text-sm border border-green-500 dark:border-green-600 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 flex items-center gap-1.5"
                    >
                      <Upload className="w-4 h-4" /> Importar
                    </button>
                    <button
                      onClick={() => setShowInfoModal(true)}
                      className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full"
                      title="Informações sobre o formato do Excel"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                </PermissionGate>
                <PermissionGate tela="HOSPITAIS_LIST" acao="exportar">
                  <button
                    onClick={onExportExcel}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Exportar
                  </button>
                </PermissionGate>
              </>
            ) : (
              <>
                {podeSalvar && (
                  <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                )}
                <button
                  onClick={onCancel}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <XIcon className="w-4 h-4" /> Cancelar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <HospitalInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};
